'use client';

import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, FilterX, X } from 'lucide-react';
import { CATEGORIES } from '@/lib/constants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns/format';
import { useListStore } from '@/stores/useListStore';
import { JSX } from 'react';

interface ListFilterSidebarProps {
  className?: string;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

export default function ListFilterSidebar({
  className,
  onMobileClose,
  isMobile = false,
}: ListFilterSidebarProps): JSX.Element {
  // Get values and actions from Zustand store
  const {
    category,
    priceRange,
    leadsRange,
    containsEmails,
    containsVerifiedEmails,
    containsPhones,
    lastUpdatedDays,
    setCategory,
    setPriceRange,
    setLeadsRange,
    setContainsEmails,
    setContainsVerifiedEmails,
    setContainsPhones,
    setLastUpdatedDays,
    resetFilters: resetAllFilters,
  } = useListStore();

  // Local UI state for form inputs
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(category);
  const [localPriceRange, setLocalPriceRange] = useState<{ min: string; max: string }>({
    min: priceRange.min?.toString() || '',
    max: priceRange.max?.toString() || '',
  });
  const [localLeadsRange, setLocalLeadsRange] = useState<{ min: string; max: string }>({
    min: leadsRange.min?.toString() || '',
    max: leadsRange.max?.toString() || '',
  });
  const [date, setDate] = useState<Date | undefined>(
    lastUpdatedDays ? new Date(Date.now() - lastUpdatedDays * 24 * 60 * 60 * 1000) : undefined,
  );

  // Update local state when store changes
  useEffect(() => {
    setSelectedCategory(category);
    setLocalPriceRange({
      min: priceRange.min?.toString() || '',
      max: priceRange.max?.toString() || '',
    });
    setLocalLeadsRange({
      min: leadsRange.min?.toString() || '',
      max: leadsRange.max?.toString() || '',
    });
    setDate(
      lastUpdatedDays ? new Date(Date.now() - lastUpdatedDays * 24 * 60 * 60 * 1000) : undefined,
    );
  }, [category, priceRange, leadsRange, lastUpdatedDays]);

  // Apply filters to store
  const applyFilters = () => {
    // Update price range
    setPriceRange(
      localPriceRange.min ? parseFloat(localPriceRange.min) : undefined,
      localPriceRange.max ? parseFloat(localPriceRange.max) : undefined,
    );

    // Update leads range
    setLeadsRange(
      localLeadsRange.min ? parseFloat(localLeadsRange.min) : undefined,
      localLeadsRange.max ? parseFloat(localLeadsRange.max) : undefined,
    );

    // Close mobile sidebar if on mobile
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Apply a specific filter change immediately
  const applyFilterChange = (filterChange: () => void) => {
    filterChange();
    applyFilters();
  };

  // Reset all filters
  const resetFilters = () => {
    // Reset local state
    setSelectedCategory(undefined);
    setLocalPriceRange({ min: '', max: '' });
    setLocalLeadsRange({ min: '', max: '' });
    setDate(undefined);

    // Reset store state
    resetAllFilters();

    // Close mobile sidebar if on mobile
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Date options for "Last Updated"
  const dateOptions = [
    { days: 0, label: 'Today', value: '0_days' },
    { days: 7, label: 'Last 7 days', value: '7_days' },
    { days: 30, label: 'Last 30 days', value: '1_month' },
    { days: 90, label: 'Last 3 months', value: '3_months' },
    { days: 180, label: 'Last 6 months', value: '6_months' },
    { days: 365, label: 'Last year', value: '1_year' },
  ];

  // Toggle category selection
  const toggleCategory = (categoryValue: string) => {
    const newCategory = selectedCategory === categoryValue ? undefined : categoryValue;
    setSelectedCategory(newCategory);
    setCategory(newCategory);
  };

  // Handle date selection for "Last Updated"
  const handleDateSelect = (newDate: Date | undefined) => {
    setDate(newDate);

    if (newDate) {
      const daysDiff = Math.round((Date.now() - newDate.getTime()) / (1000 * 60 * 60 * 24));

      // Map the daysDiff to the appropriate option value
      let formatValue;
      if (daysDiff === 0) {
        formatValue = '0_days'; // Today
      } else if (daysDiff <= 7) {
        formatValue = `${daysDiff}_day${daysDiff > 1 ? 's' : ''}`;
      } else if (daysDiff <= 28) {
        const weeks = Math.round(daysDiff / 7);
        formatValue = `${weeks}_week${weeks > 1 ? 's' : ''}`;
      } else if (daysDiff <= 365) {
        const months = Math.round(daysDiff / 30);
        formatValue = `${months}_month${months > 1 ? 's' : ''}`;
      } else {
        const years = Math.round(daysDiff / 365);
        formatValue = `${years}_year${years > 1 ? 's' : ''}`;
      }

      applyFilterChange(() => {
        setLastUpdatedDays(daysDiff);

        // If we had a direct setter for the format value, we could use it here
        // For now, we're just using the daysDiff as before
      });
    } else {
      applyFilterChange(() => {
        setLastUpdatedDays(undefined);
      });
    }
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return !!(
      category ||
      priceRange.min !== undefined ||
      priceRange.max !== undefined ||
      leadsRange.min !== undefined ||
      leadsRange.max !== undefined ||
      containsEmails ||
      containsVerifiedEmails ||
      containsPhones ||
      lastUpdatedDays !== undefined
    );
  };

  return (
    <div className={cn('space-y-5', className)}>
      {/* Categories Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Categories</h3>
          {selectedCategory && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSelectedCategory(undefined);
                setCategory(undefined);
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-2">
          {CATEGORIES.map((cat) => (
            <div
              key={cat}
              className={cn(
                'flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors',
                selectedCategory === cat ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
              )}
              onClick={() => toggleCategory(cat)}
            >
              <span className="text-sm">{cat}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Price Range Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Price Range</h3>
          {(localPriceRange.min || localPriceRange.max) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setLocalPriceRange({ min: '', max: '' });
                setPriceRange(undefined, undefined);
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="price-min" className="text-xs">
              Min ($)
            </Label>
            <Input
              id="price-min"
              type="number"
              min="0"
              step="1"
              placeholder="Min"
              value={localPriceRange.min}
              onChange={(e) => setLocalPriceRange({ ...localPriceRange, min: e.target.value })}
              onBlur={() => applyFilters()}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="price-max" className="text-xs">
              Max ($)
            </Label>
            <Input
              id="price-max"
              type="number"
              min="0"
              step="1"
              placeholder="Max"
              value={localPriceRange.max}
              onChange={(e) => setLocalPriceRange({ ...localPriceRange, max: e.target.value })}
              onBlur={() => applyFilters()}
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Leads Range Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Leads Count</h3>
          {(localLeadsRange.min || localLeadsRange.max) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setLocalLeadsRange({ min: '', max: '' });
                setLeadsRange(undefined, undefined);
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="leads-min" className="text-xs">
              Min
            </Label>
            <Input
              id="leads-min"
              type="number"
              min="0"
              step="1"
              placeholder="Min"
              value={localLeadsRange.min}
              onChange={(e) => setLocalLeadsRange({ ...localLeadsRange, min: e.target.value })}
              onBlur={() => applyFilters()}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="leads-max" className="text-xs">
              Max
            </Label>
            <Input
              id="leads-max"
              type="number"
              min="0"
              step="1"
              placeholder="Max"
              value={localLeadsRange.max}
              onChange={(e) => setLocalLeadsRange({ ...localLeadsRange, max: e.target.value })}
              onBlur={() => applyFilters()}
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Contains Data Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <h3 className="text-sm font-semibold">Data Filters</h3>
        <div className="space-y-2.5">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="contains-emails"
              checked={containsEmails}
              onCheckedChange={(checked) =>
                applyFilterChange(() => {
                  setContainsEmails(checked === true);
                })
              }
            />
            <Label htmlFor="contains-emails" className="text-sm cursor-pointer">
              Contains Emails
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="contains-verified-emails"
              checked={containsVerifiedEmails}
              onCheckedChange={(checked) =>
                applyFilterChange(() => {
                  setContainsVerifiedEmails(checked === true);
                })
              }
            />
            <Label htmlFor="contains-verified-emails" className="text-sm cursor-pointer">
              Contains Verified Emails
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="contains-phones"
              checked={containsPhones}
              onCheckedChange={(checked) =>
                applyFilterChange(() => {
                  setContainsPhones(checked === true);
                })
              }
            />
            <Label htmlFor="contains-phones" className="text-sm cursor-pointer">
              Contains Phones
            </Label>
          </div>
        </div>
      </div>

      {/* Last Updated Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Last Updated</h3>
          {(lastUpdatedDays || date) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() =>
                applyFilterChange(() => {
                  setLastUpdatedDays(undefined);
                  setDate(undefined);
                })
              }
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="space-y-2.5">
          {dateOptions.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`last-updated-${option.value}`}
                checked={lastUpdatedDays === option.days}
                onCheckedChange={(checked) => {
                  if (checked) {
                    const days = option.days;
                    setDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));
                    applyFilterChange(() => {
                      setLastUpdatedDays(days);
                    });
                  } else if (lastUpdatedDays === option.days) {
                    setDate(undefined);
                    applyFilterChange(() => {
                      setLastUpdatedDays(undefined);
                    });
                  }
                }}
              />
              <Label htmlFor={`last-updated-${option.value}`} className="text-sm cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal h-8 text-sm',
                  !date && 'text-muted-foreground',
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, 'PPP') : 'Custom date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={date} onSelect={handleDateSelect} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col space-y-2 pt-2">
        {hasActiveFilters() && (
          <Button
            variant="outline"
            onClick={resetFilters}
            size={isMobile ? 'default' : 'sm'}
            className="flex items-center justify-center gap-1.5 w-full"
          >
            <FilterX className="h-4 w-4" />
            Reset All Filters
          </Button>
        )}
      </div>
    </div>
  );
}
